// src/pages/Dashboard.js
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store';
import { analyticsAPI } from '../utils/api';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, postsSummary, fetchSummary } = useStore();
  const [byAccounts, setByAccounts] = React.useState([]);

  useEffect(() => {
    fetchSummary();
    analyticsAPI.byAccounts().then(r => setByAccounts(r.data.accounts)).catch(() => {});
  }, []);

  const summaryCards = [
    { icon: '🕐', label: 'Queued Posts', value: postsSummary.queued || 0, color: '#555' },
    { icon: '✅', label: 'Delivered (30 Days)', value: postsSummary.delivered_30 || 0, color: '#0f766e' },
    { icon: '📅', label: 'Unscheduled Posts', value: postsSummary.drafts || 0, color: '#555' },
    { icon: '⚠', label: 'Error Posts', value: postsSummary.errors || 0, color: '#e55b2d' },
  ];

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h1 style={{ fontSize: 18, fontWeight: 600, color: '#1a1f2e' }}>Hey, {user?.name} 👋</h1>
        <span style={{ fontSize: 12, color: '#0f766e', cursor: 'pointer', fontWeight: 500 }}>Edit Dashboard</span>
      </div>

      <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1f2e', marginBottom: 10 }}>Publishing Summary</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
        {summaryCards.map(c => (
          <div key={c.label} className="card" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 22, color: c.color }}>{c.icon}</span>
            <div><div style={{ fontSize: 22, fontWeight: 700, color: '#1a1f2e', lineHeight: 1 }}>{c.value}</div><div style={{ fontSize: 11, color: '#8892a4', marginTop: 2 }}>{c.label}</div></div>
          </div>
        ))}
      </div>

      {/* Publishing Trends */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">
          <div>
            <div className="card-title">Publishing Trends</div>
            <div style={{ fontSize: 11, color: '#8892a4', marginTop: 2 }}>Total Posts: {(postsSummary.queued || 0) + (postsSummary.delivered_30 || 0)}</div>
          </div>
          <button className="filter-pill">⚡ Filter By <strong style={{ marginLeft: 4 }}>Current</strong> ▾</button>
        </div>
        <div style={{ padding: '14px 16px' }}>
          <svg width="100%" height="100" viewBox="0 0 600 100" preserveAspectRatio="none">
            <defs><linearGradient id="cg2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0f766e" stopOpacity=".25"/><stop offset="100%" stopColor="#0f766e" stopOpacity="0"/></linearGradient></defs>
            <path d="M0,80 L40,20 L80,65 L120,50 L160,15 L200,60 L240,40 L280,10 L320,50 L360,70 L400,45 L440,75 L480,60 L520,72 L560,65 L600,78" fill="none" stroke="#0f766e" strokeWidth="2"/>
            <path d="M0,80 L40,20 L80,65 L120,50 L160,15 L200,60 L240,40 L280,10 L320,50 L360,70 L400,45 L440,75 L480,60 L520,72 L560,65 L600,78 L600,100 L0,100 Z" fill="url(#cg2)"/>
            {['Jun 10','Jun 14','Jun 19','today','Jun 29','Jul 04'].map((l,i) => <text key={i} x={i*110} y={98} fontSize={8} fill="#aaa">{l}</text>)}
          </svg>
        </div>
      </div>

      {/* Publishing by Accounts */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header"><span className="card-title">Publishing by Accounts</span></div>
        <table className="data-table">
          <thead><tr><th>Account Name</th><th>Planned</th><th>Errors</th><th>Planned Until</th></tr></thead>
          <tbody>
            {byAccounts.length ? byAccounts.map((a, i) => (
              <tr key={i}>
                <td style={{ fontWeight: 500 }}>{a.account_name}</td>
                <td><span style={{ color: '#0f766e', fontWeight: 600, cursor: 'pointer' }}>{a.planned}</span></td>
                <td><span style={{ color: a.errors > 0 ? '#e55b2d' : '#555', fontWeight: 600, cursor: 'pointer' }}>{a.errors}</span></td>
                <td style={{ color: a.planned_until ? '#555' : '#aaa' }}>{a.planned_until ? new Date(a.planned_until).toLocaleDateString('en-AU') : 'Not Scheduled'}</td>
              </tr>
            )) : (
              <tr><td colSpan={4}><div style={{ textAlign: 'center', padding: '20px', color: '#aaa', fontSize: 12 }}>No account data yet — connect accounts and schedule posts</div></td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Tasks */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div className="card">
          <div className="card-header"><span className="card-title">Tasks</span></div>
          <div style={{ padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span style={{ color: '#e55b2d', fontSize: 18, flexShrink: 0 }}>⬛</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1f2e' }}>Connect your social accounts</div>
              <div style={{ fontSize: 11, color: '#8892a4', marginTop: 2 }}>Accounts that have been disconnected and require reconnection.</div>
              <button className="btn btn-primary" style={{ marginTop: 8, fontSize: 11 }} onClick={() => navigate('/accounts/connect')}>Connect Account</button>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><span className="card-title">Publishing by Clients</span></div>
          <table className="data-table"><thead><tr><th>Clients</th><th>Planned</th><th>Idle</th><th>Planned Until</th></tr></thead><tbody><tr><td colSpan={4} style={{ textAlign: 'center', color: '#aaa', fontSize: 11, padding: 16 }}>No clients yet</td></tr></tbody></table>
        </div>
      </div>

    </div>
  );
}
